import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { getArticleSchema, getBreadcrumbSchema } from '@/components/base/SEOHead';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (article) {
      const articleLD = document.createElement('script');
      articleLD.type = 'application/ld+json';
      articleLD.textContent = JSON.stringify(
        getArticleSchema({
          title: article.title,
          description: article.excerpt,
          url: `https://www.huatiyuiguanwang.com/article/${article.id}`,
          datePublished: article.date,
          author: article.author,
        }),
        null,
        2
      );
      document.head.appendChild(articleLD);

      const breadcrumbLD = document.createElement('script');
      breadcrumbLD.type = 'application/ld+json';
      breadcrumbLD.textContent = JSON.stringify(
        getBreadcrumbSchema([
          { name: '首页', url: 'https://www.huatiyuiguanwang.com/' },
          { name: article.categoryName, url: `https://www.huatiyuiguanwang.com/category/${article.category}` },
          { name: article.title, url: `https://www.huatiyuiguanwang.com/article/${article.id}` },
        ]),
        null,
        2
      );
      document.head.appendChild(breadcrumbLD);

      return () => {
        document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
          if (el.textContent?.includes('Article') || el.textContent?.includes('BreadcrumbList')) {
            el.remove();
          }
        });
      };
    }
  }, [id, article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-accent-700 hover:text-accent-800 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.categoryName, path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-medium text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full">
                {article.categoryName}
              </span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-snug mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-full">
                <i className="ri-user-line text-accent-700 text-sm"></i>
              </div>
              <div>
                <span className="text-sm font-medium text-foreground-900">{article.author}</span>
                <span className="text-xs text-foreground-500 ml-3">{article.date}</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-accent-300 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{rel.date}</span>
                        <span className="text-xs text-foreground-500">{rel.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 leading-snug group-hover:text-accent-700 transition-colors duration-200 line-clamp-2">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}